import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import "./VisionModalPopComponent.css";

/**
 * VisionModalPopComponent - A custom div-based modal for Vision Portal
 * Built with pure HTML elements to match VDS Modal appearance
 *
 * Features:
 * - Custom header with close button
 * - Responsive body with scrolling support
 * - Configurable primary and cancel buttons
 * - Mobile-responsive design
 * - Overlay backdrop
 * - ESC key and outside click support
 *
 * @param {Object} props
 * @param {boolean} props.opened - Controls modal visibility
 * @param {string} props.title - Modal header title
 * @param {React.ReactNode} props.children - Modal body content
 * @param {Object} props.primaryButton - Primary button configuration
 * @param {string} props.primaryButton.label - Button text
 * @param {Function} props.primaryButton.onClick - Click handler
 * @param {boolean} props.primaryButton.disabled - Disabled state
 * @param {string} props.primaryButton.width - Button width (default: "350px")
 * @param {Object} props.closeButton - Close button configuration
 * @param {string} props.closeButton.label - Button text (default: "Cancel")
 * @param {Function} props.closeButton.onClick - Click handler
 * @param {string} props.closeButton.width - Button width (default: "350px")
 * @param {string} props.ariaLabel - Accessibility label
 * @param {boolean} props.disableOutsideClick - Prevent closing on outside click (default: false)
 */
const VisionModalPopComponent = ({
  opened,
  title,
  children,
  primaryButton,
  closeButton,
  ariaLabel,
  disableOutsideClick = false,
}) => {
  const modalRef = useRef(null);
  const overlayRef = useRef(null);

  // Handle ESC key press
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && opened && closeButton?.onClick) {
        closeButton.onClick();
      }
    };

    if (opened) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [opened, closeButton]);

  // Handle outside click
  const handleOverlayClick = (event) => {
    if (
      !disableOutsideClick &&
      overlayRef.current === event.target &&
      closeButton?.onClick
    ) {
      closeButton.onClick();
    }
  };

  if (!opened) return null;

  return (
    <div
      className="vision-modal-overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel || title}
    >
      <div className="vision-modal-container" ref={modalRef}>
        {/* Modal Header */}
        <div className="vision-modal-header">
          <div className="vision-modal-title">{title}</div>
          {closeButton?.onClick && (
            <button
              className="vision-modal-close-btn"
              onClick={closeButton.onClick}
              aria-label="Close modal"
              title="Close"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Modal Body */}
        <div className="vision-modal-body">
          <div className="vision-modal-content">{children}</div>
        </div>

        {/* Modal Footer */}
        {(primaryButton || closeButton) && (
          <div className="vision-modal-footer">
            <div className="vision-modal-button-group">
              {primaryButton && (
                <button
                  className="vision-modal-btn vision-modal-btn-primary"
                  onClick={primaryButton.onClick}
                  disabled={primaryButton.disabled || false}
                  style={{ width: primaryButton.width || "350px" }}
                  aria-label={primaryButton.label}
                >
                  {primaryButton.label}
                </button>
              )}
              {closeButton && (
                <button
                  className="vision-modal-btn vision-modal-btn-secondary"
                  onClick={closeButton.onClick}
                  style={{ width: closeButton.width || "350px" }}
                  aria-label={closeButton.label || "Cancel"}
                >
                  {closeButton.label || "Cancel"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

VisionModalPopComponent.propTypes = {
  opened: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  primaryButton: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
    width: PropTypes.string,
  }),
  closeButton: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func.isRequired,
    width: PropTypes.string,
  }),
  ariaLabel: PropTypes.string,
  disableOutsideClick: PropTypes.bool,
};

export default VisionModalPopComponent;
